// Five before and after layouts, from the transformation kit: the editorial
// split, the stacked reveal, the after spotlight, the contact sheet and the
// studio prints. The same client's two photos, labelled in every one.
import { flow, textBlock, brand, footer, rule, photo, photoOf, tag, display, label, strong } from '../../kit'
import { dryPainter } from '../../painter'
import { base, ground, serif, detailLine } from './common'

const who = (env, fr, colour) => detailLine(env, fr, env.post.content.detail, colour)

// A label on a block of colour, set in from the right edge
const tagRight = (pp, env, text, right, y, bg, fg) => {
  const m = dryPainter(pp).text(text, { ...label(env, fg), x: 0, y: 0, w: 880 })
  return tag(pp, env, text, right - Math.min(936, m.width + 44), y, bg, fg)
}

// A photo inside a turned print: drawn with the turn, tapped where it sits
const turnedPhoto = (pp, env, key, box, at) => {
  const { ph, img } = photoOf(env, key)
  pp.image(img, box, ph, { bg: env.theme.c.holder, ink: env.theme.c.holderInk })
  if (!pp.isDry) pp.boxes.push({ slot: `photo:${key}`, ...at, photo: true })
}

export default {
  // Two equal portraits side by side, under a quiet headline
  split(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock('The transformation edit', () => ({ ...label(env, ink), x: fr.left, w }), { gap: 60 }),
      textBlock(c.headline, s => ({ ...display(env, ink, 112 * s), x: fr.left - 5, w: w + 10, maxLines: 2 }), { gap: 30, slot: 'headline' }),
      rule(fr, ink, { gap: 44 }),
      {
        gap: 36,
        flex: 1,
        draw: (pp, y, s) => {
          const h = Math.round(630 * s)
          const gap = 24
          const pw = (w - gap) / 2
          ;[['before', 'Before'], ['after', 'After']].forEach(([key, name], i) => {
            const x = fr.left + i * (pw + gap)
            photo(pp, env, key, { x, y, w: pw, h })
            if (!pp.isDry) tag(pp, env, name, x + 18, y + 18, k.ink, k.paper)
          })
          return y + h
        },
      },
      who(env, fr, ink),
      footer(env, fr, ink),
    ])
  },

  // Before, then after, on two wide cards
  stacked(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const card = (key, name, n, bg, fg) => ({
      gap: key === 'before' ? 50 : 40,
      flex: key === 'before' ? 1 : 0,
      draw: (pp, y, s) => {
        const h = Math.round(353 * s)
        const pw = Math.round(w * 0.415)
        pp.rect(fr.left, y, w, h, bg)
        photo(pp, env, key, { x: fr.right - pw, y, w: pw, h })
        const num = pp.text(`0${n} /`, { ...strong(env, fg, 40), x: fr.left + 30, y: y + Math.round(34 * s), w: w - pw - 60, maxLines: 1 })
        pp.text(name, { ...display(env, fg, Math.round(68 * s)), upper: true, minSize: 40, x: fr.left + 30, y: Math.max(num.bottom + 20, y + Math.round(130 * s)), w: w - pw - 60, maxLines: 1 })
        return y + h
      },
    })
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.headline, s => ({ ...display(env, ink, 94 * s), x: fr.left - 4, w: w + 8, maxLines: 2 }), { gap: 52, slot: 'headline' }),
      card('before', 'Before', 1, k.pop, k.ink),
      card('after', 'After', 2, k.accent, k.on),
      who(env, fr, ink),
      footer(env, fr, ink),
    ])
  },

  // The finished look, big, with the before in a small print on top
  afterSpotlight(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.headline, s => ({ ...display(env, ink, 100 * s), x: fr.left - 4, w: w + 8, maxLines: 3 }), { gap: 40, slot: 'headline' }),
      {
        gap: 30,
        flex: 1,
        draw: (pp, y, s) => {
          const h = Math.round(748 * s)
          photo(pp, env, 'after', { x: fr.left, y, w, h })
          if (pp.isDry) return y + h
          tagRight(pp, env, 'After', fr.right - 18, y + 22, k.ink, k.paper)
          const iw = Math.round(400 * Math.max(s, 0.8))
          const ih = Math.round(390 * s)
          const ix = fr.left + 14
          const iy = y + h - 18 - ih
          const pad = 13
          pp.rect(ix, iy, iw, ih, k.paper)
          photo(pp, env, 'before', { x: ix + pad, y: iy + pad, w: iw - pad * 2, h: ih - pad * 2 - 48 })
          tag(pp, env, 'Before', ix + pad + 14, iy + pad + 14, k.ink, k.paper)
          pp.text('The starting point', { ...label(env, k.ink), x: ix + pad + 4, y: iy + ih - 46, w: iw - pad * 2 - 8 })
          return y + h
        },
      },
      who(env, fr, ink),
      footer(env, fr, ink),
    ])
  },

  // Two frames of film, staggered, on a dark ground
  contactSheet(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.ink)
    const on = k.paper
    flow(p, fr, [
      brand(env, fr, on),
      textBlock(c.headline, s => ({ ...display(env, on, 112 * s), x: fr.left - 4, w: w + 8, maxLines: 2 }), { gap: 60, slot: 'headline' }),
      {
        gap: 52,
        flex: 1,
        draw: (pp, y, s) => {
          const gap = 68
          const fw = (w - gap) / 2
          const fh = Math.round(674 * s)
          const stagger = Math.round(69 * s)
          ;[['before', 'Before'], ['after', 'After']].forEach(([key, name], i) => {
            const x = fr.left + i * (fw + gap)
            const top = y + i * stagger
            pp.rect(x, top, fw, fh, k.paper)
            photo(pp, env, key, { x: x + 16, y: top + Math.round(40 * s), w: fw - 32, h: Math.round(530 * s) })
            const step = (fw - 40) / 11
            for (let h = 0; h < 11; h += 1) {
              pp.rect(x + 20 + h * step, top + Math.round(12 * s), 19, 12, k.ink)
              pp.rect(x + 20 + h * step, top + Math.round(587 * s), 19, 12, k.ink)
            }
            pp.text(`0${i + 1} / ${name}`, { ...label(env, k.ink), x: x + 21, y: top + fh - 54, w: fw - 42 })
          })
          return y + stagger + fh
        },
      },
      who(env, fr, on),
      footer(env, fr, on),
    ])
  },

  // Two prints, pinned at an angle, each with its word in a serif
  prints(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const turn = 0.075
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.headline, s => ({ ...display(env, ink, 102 * s), x: fr.left - 4, w: w + 8, maxLines: 3 }), { gap: 44, slot: 'headline' }),
      {
        gap: 60,
        flex: 1,
        draw: (pp, y, s) => {
          const pw = Math.round(476 * s)
          const ph = Math.round(640 * s)
          const drop = Math.round(55 * s)
          const lean = Math.round(Math.sin(turn) * pw / 2)
          if (!pp.isDry) {
            const { ctx } = pp
            ;[['before', 'Before.', fr.left - 21 + pw / 2, -turn, 0], ['after', 'After.', fr.right - 12 - pw / 2 + 14, turn, drop]].forEach(([key, word, cx, a, down]) => {
              const cy = y + lean + down + ph / 2
              ctx.save()
              ctx.translate(cx, cy)
              ctx.rotate(a)
              pp.rect(-pw / 2, -ph / 2, pw, ph, k.pop)
              turnedPhoto(pp, env, key, { x: -pw / 2 + 19 * s, y: -ph / 2 + 19 * s, w: pw - 38 * s, h: Math.round(501 * s) }, { x: cx - pw / 2, y: cy - ph / 2, w: pw, h: ph })
              pp.text(word, { ...serif(env, k.ink, Math.round(54 * s)), minSize: 34, x: -pw / 2 + 31 * s, y: ph / 2 - Math.round(92 * s), w: pw - 60 * s, maxLines: 1 })
              ctx.restore()
            })
          }
          return y + lean * 2 + drop + ph
        },
      },
      who(env, fr, ink),
      footer(env, fr, ink),
    ])
  },
}
